"use client";

import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { formatPrice } from "@/lib/format-price";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type PriceBlok = SbBlokData & {
  name?: string;
  description?: string;
  price_ie?: string;
  price_uk?: string;
  monthly_price_ie?: string;
  monthly_price_uk?: string;
  features?: string;
  learn_more_link?: StoryblokLink;
  learn_more_label?: string;
};

function CheckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden
      className="mt-[3px] size-[18px] shrink-0 text-green-600"
    >
      <path
        d="M3 9.5l3.5 3.5L15 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Price({ blok }: { blok: PriceBlok }) {
  const ie = formatPrice(blok.price_ie, "ie");
  const uk = formatPrice(blok.price_uk, "uk");
  const monthlyIe = formatPrice(blok.monthly_price_ie, "ie");
  const monthlyUk = formatPrice(blok.monthly_price_uk, "uk");
  const features = (blok.features || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const region = regionFromPathname(usePathname());
  const learnMore = resolveLink(blok.learn_more_link, region);
  const learnMoreLabel =
    blok.learn_more_label || (blok.name ? `More about ${blok.name}` : "Learn more");

  return (
    <section {...storyblokEditable(blok)} className="mx-auto max-w-md px-5 py-10">
      <div className="rounded-[20px] bg-white p-6 text-black ring-1 ring-black/10">
        {blok.name && <div className="text-h6 font-bold tracking-[0.02em]">{blok.name}</div>}
        {blok.description && (
          <p className="text-button mt-2 leading-[1.4] text-black/70">{blok.description}</p>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <div className="text-caption font-semibold tracking-wide text-black/60 uppercase">
              Ireland
            </div>
            {ie && (
              <div className="text-h4 text-purple-03 mt-1 leading-none font-extrabold tracking-[0.02em]">
                {ie}
              </div>
            )}
            {monthlyIe && (
              <div className="text-button mt-1 text-black/70">or from {monthlyIe} p/month</div>
            )}
          </div>
          <div>
            <div className="text-caption font-semibold tracking-wide text-black/60 uppercase">
              United Kingdom
            </div>
            {uk && (
              <div className="text-h4 text-purple-03 mt-1 leading-none font-extrabold tracking-[0.02em]">
                {uk}
              </div>
            )}
            {monthlyUk && (
              <div className="text-button mt-1 text-black/70">or from {monthlyUk} p/month</div>
            )}
          </div>
        </div>

        {features.length > 0 && (
          <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {features.map((feat, i) => (
              <li key={`${i}-${feat}`} className="flex items-start gap-2">
                <CheckIcon />
                <span className="text-body leading-tight">{feat}</span>
              </li>
            ))}
          </ul>
        )}

        {learnMore.href !== "#" && (
          <a
            href={learnMore.href}
            {...(learnMore.target ? { target: learnMore.target } : {})}
            rel={learnMore.target === "_blank" ? "noopener noreferrer" : undefined}
            className="text-body text-purple-04 mt-6 inline-flex w-fit items-center gap-[7px] py-2.5 leading-[1.054] font-extrabold hover:underline"
          >
            {learnMoreLabel}
            <ArrowIcon />
          </a>
        )}
      </div>
    </section>
  );
}
